/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { useNavigate } from '@tanstack/react-router';
import React, { useEffect } from 'react';
import useLocalStorageState from 'use-local-storage-state';

import { useTranslation } from '~/app';
import { localeCookieName, localeLocalStorageKey, STORAGE_KEY_ALIASES } from '~/app/storageKeys';
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '~/components/DropdownMenu/DropdownMenu';
import { cn } from '~/utils/styles';

// Get localized language name using browser's Intl API
const getLocalizedLanguageName = (languageCode: string, currentLocale: string): string => {
  try {
    // Use Intl.DisplayNames to get the language name in the current locale
    const displayNames = new Intl.DisplayNames([currentLocale], { type: 'language' });
    return displayNames.of(languageCode) || languageCode.toUpperCase();
  } catch {
    // Fallback to uppercase language code if Intl.DisplayNames is not supported
    return languageCode.toUpperCase();
  }
};

// Language icon component
const LanguageIcon = () => (
  <svg
    className="h-5 w-5 shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
    />
  </svg>
);

// Check icon component
const CheckIcon = ({ className }: { className?: string }) => (
  <svg
    className={cn('h-4 w-4', className)}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const LanguageSubmenu = (): JSX.Element => {
  const navigate = useNavigate();
  const { t, allLocales, urlPath, locale } = useTranslation();
  const [, setLocale] = useLocalStorageState(localeLocalStorageKey, { defaultValue: 'de' });

  // Persist locale to cookie when it changes (avoids modifying document in event handler)
  useEffect(() => {
    try {
      const maxAge = 60 * 60 * 24 * 365;
      document.cookie = `${localeCookieName}=${locale}; path=/; max-age=${maxAge}`;
      document.cookie = `${STORAGE_KEY_ALIASES.localeCookie[0]}=${locale}; path=/; max-age=${maxAge}`;
    } catch {
      /* ignore cookie errors (e.g. private mode) */
    }
  }, [locale]);

  const handleLanguageSelect = (pLocale: string) => {
    setLocale(pLocale);
    // Since URLs are English-only, keep path and refresh translations
    navigate({ to: urlPath, replace: true });
  };

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="flex w-full min-w-0 cursor-pointer flex-row items-center justify-start space-x-4 rounded-md px-3 py-2 text-left font-sans text-base font-medium transition-colors hover:bg-primary-light/20 focus:bg-primary-light/20 focus:outline-hidden sm:px-4 sm:py-3 sm:text-lg">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center">
          <LanguageIcon />
        </span>
        <span className="min-w-0 flex-1 truncate text-left leading-none">
          {t('start_languageSelectionHeader')}
        </span>
      </DropdownMenuSubTrigger>

      <DropdownMenuSubContent className="z-50 min-w-[200px] rounded-lg border border-gray-200 bg-white p-1 shadow-xl sm:min-w-[240px] sm:p-2">
        {allLocales.map(({ locale: pLocale, country: pCountry }) => (
          <DropdownMenuItem
            key={pLocale}
            onClick={() => handleLanguageSelect(pLocale)}
            className={cn(
              'flex w-full cursor-pointer flex-row items-center justify-start space-x-4 rounded-md px-3 py-2 text-left font-sans text-base font-medium transition-colors hover:bg-primary-light/20 focus:bg-primary-light/20 focus:outline-hidden sm:px-4 sm:py-3 sm:text-lg',
              pLocale === locale && 'bg-primary-light/20',
            )}
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center text-xs font-bold">
              {pCountry}
            </span>
            <span className="flex flex-1 items-center">
              {getLocalizedLanguageName(pLocale, locale)}
            </span>
            {pLocale === locale && <CheckIcon className="ml-auto h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};

export default LanguageSubmenu;
